import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EnvironmentVariablesService } from '@kenv';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { GroupValidationBloc } from '@kp/auth/group-validation.bloc';
import { DataLinkBloc } from '@kp/data-link/data-link.bloc';
import { Constants } from '@kp/shared/constants.service';
import { FirstVisitBloc } from '@kp/shared/first-visit/first-visit.bloc';
import { UserBloc } from '@kp/user/user.bloc';
import { AuthDataService, DataStoreService, OnboardingUtilities, productDisplayName, WINDOW } from '@kservice';
import { Product, UserType } from '@ktypes/enums';
import { AuthData, Group, Status } from '@ktypes/models';
import { EnumUtil } from '@kutil';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, Subject, Subscription } from 'rxjs';

@Component({
  selector: 'kp-screen-thank-you',
  templateUrl: './screen-thank-you.component.html',
  styleUrls: ['./screen-thank-you.component.scss'],
})
export class ScreenThankYouComponent implements OnInit, OnDestroy {
  animating = true;
  doNotOfferApp = false;
  gettingStartedError = false;
  groupCode: string;
  loading = true;
  userType: UserType;

  private _buttonClicked = new Subject();
  private _subscriptions: Subscription[] = [];

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _authDataService: AuthDataService,
    private _authenticationBloc: AuthenticationBloc,
    private _dataLinkBloc: DataLinkBloc,
    private _dataStoreService: DataStoreService,
    private _deviceDetectorService: DeviceDetectorService,
    private _envVariablesService: EnvironmentVariablesService,
    private _firstVisitBloc: FirstVisitBloc,
    private _groupValidationBloc: GroupValidationBloc,
    private _onboardingUtilities: OnboardingUtilities,
    private _router: Router,
    private _userBloc: UserBloc,
    @Inject(WINDOW) private _window: Window
  ) {}

  ngOnInit() {
    this.userType = EnumUtil.getByValue(UserType, this._activatedRoute.snapshot.queryParamMap.get('user_type'));
    this.groupCode = this._activatedRoute.snapshot.queryParamMap.get('group_code');
    this.doNotOfferApp = this._envVariablesService.product === Product.resourceful;
    let skipDone = '';
    try {
      skipDone = JSON.parse(skipDone);
    } catch (e) {
      skipDone = this._activatedRoute.snapshot.queryParamMap.get('skip_done');
    }
    if (skipDone) {
      this.animating = false;
      this._redirectOrContinue();
    }
    // prevent page flash in redirect scenarios
    setTimeout(() => {
      this.loading = false;
    }, 500);

    this._subscriptions.push(
      combineLatest([
        this._dataStoreService.authStatus$,
        this._groupValidationBloc.groupValidationDataStatus$,
        this._buttonClicked,
      ]).subscribe(([onboardingUserStatus, groupValidationStatus, buttonClicked]) => {
        if (
          buttonClicked &&
          onboardingUserStatus != null &&
          onboardingUserStatus?.status === Status.done &&
          onboardingUserStatus?.data != null &&
          groupValidationStatus &&
          groupValidationStatus?.status === Status.done &&
          groupValidationStatus?.data != null
        ) {
          this.gettingStartedError = false;
          if (
            (this._deviceDetectorService.isMobile() || this._deviceDetectorService.isTablet()) &&
            !this.doNotOfferApp
          ) {
            const appReturnLink = encodeURIComponent(
              Constants.mobileTransferDynamicLinkTemplate
                .replace('$windowLocationOrigin', this._window.location.origin)
                .replace(/\$groupCode/g, this.groupCode)
                .replace('$userId', onboardingUserStatus?.data?.user?.id)
            );
            const firebaseQueryParams = Constants.mobileTransferDynamicLinkQueryParamsTemplate
              .replace('$socialTitle', `Welcome to ${productDisplayName}`)
              .replace('$socialCTA', 'Follow the link below to download the app and create your account.');
            this._window.location.href = `${Constants.mobileTransferLinkBase}${appReturnLink}${firebaseQueryParams}`;
          } else {
            this._clearPulseSurveyAuthData();
            void this._router.navigate(['/welcome/notice']);
          }
          // reset button click
          this._buttonClicked.next(false);
        } else if (onboardingUserStatus && onboardingUserStatus.status === Status.error) {
          this.gettingStartedError = true;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((subscription) => subscription && subscription.unsubscribe());
  }

  isAnimating(event: boolean) {
    if (this.animating) {
      this.animating = event;
      if (this.userType === UserType.user) {
        this._redirectOrContinue();
      }
    }
  }

  getStarted() {
    const authData = this._authDataService.loadToken();
    const userId = authData?.user.id || authData?.userId;
    this._authenticationBloc.clearExistingAuthData();
    this._onboardingUtilities.saveOnboardingData({
      groupCode: this.groupCode,
      latestPage: '/welcome/notice',
    });
    this._userBloc.createOnboardingUser(new Group().deserialize({ groupCode: this.groupCode }), userId);
    this._groupValidationBloc.refreshGroup(this.groupCode);
    this._buttonClicked.next(true);
  }

  private _redirectOrContinue() {
    const existingAuthData = this._clearPulseSurveyAuthData();
    if ((existingAuthData?.token && existingAuthData?.user?.email) || this.userType === UserType.user) {
      if (this.userType === UserType.user) {
        this._onboardingUtilities.cleanupOnboardingStorage();
      }
      void this._router.navigate(['']);
    }
  }

  private _clearPulseSurveyAuthData(): AuthData {
    // clear out any external pulse survey info
    this._dataStoreService.setPulseSurveyAuthData(null);
    this._dataLinkBloc.clearLinkData();
    DataLinkBloc.isExternalPulseSurvey = false;
    return this._authenticationBloc.restoreExistingAuthData();
  }
}
